import React from 'react';
import { find, flatMap } from 'lodash/fp';
import { option } from 'fp-ts';
import { map } from 'fp-ts/lib/Option';
import { Route } from 'react-router-dom';
import queryString from 'query-string';
import store from 'src/store';

import { ViewComponent, BoundEnabledPerspective, BoundTab } from 'src/services/configuration/codecs/bindings.types';
import { computeTabZipRoute, computeViewZipRoute } from 'src/pages/NavigationShell/navigationUtils';

import {
  BoundTenantZipper,
  BoundTabZipper,
  BoundSectionZipper,
  BoundViewZipper,
} from 'src/pages/NavigationShell/NavigationZipper';
import ErrorBoundary from 'src/components/ErrorBoundary/ErrorBoundary';
import { pipe } from 'fp-ts/lib/function';
import { getPerspectiveLookupKey } from 'src/services/configuration/service';
import Favorite from '../Mfp/MfpGrid/favorite';
import { WithRouterProps } from 'src/components/higherOrder/withRouter';
import { DebugInfo } from 'src/components/DebugPopper/DebugPopper';
import { UIConfDefn } from 'src/services/configuration/codecs/confdefn';

function viewComponentToRouteComponent(
  id: string,
  path: string,
  viewComponent: ViewComponent,
  viewProps?: { [s: string]: unknown }
): JSX.Element {
  const Component = viewComponent;
  const modPath = path
    .split('/')
    .slice(2)
    .join('/');
  return (
    <Route
      key={id}
      path={`${modPath}/*`}
      element={
        <ErrorBoundary>
          <Component key={`${path}-${id}`} {...viewProps} />
        </ErrorBoundary>
      }
    />
  );
}

export function getActivePerspective(
  perspective: string | undefined,
  perspectives: BoundEnabledPerspective[]
): option.Option<BoundEnabledPerspective> {
  return option.fromNullable(find((x) => x.pathSlot === perspective, perspectives));
}

export function getActivePerspectiveId(
  perspective: string | undefined,
  perspectives: BoundEnabledPerspective[]
): option.Option<string> {
  return pipe(
    getActivePerspective(perspective, perspectives),
    map((p) => getPerspectiveLookupKey(p))
  );
}

function getViewContentArea(view: BoundViewZipper): JSX.Element {
  // Figure out a good way of allowing the component to access the bound view
  return viewComponentToRouteComponent(
    view.getView().id,
    computeViewZipRoute(view),
    view.getView().component,
    view.getView().componentProps
  );
}

function getSectionContentArea(section: BoundSectionZipper): JSX.Element[] {
  return section
    .getViews()
    .filter((view) => !view.getView().errorView)
    .map((view) => getViewContentArea(view));
}

function getTabContentArea(tab: BoundTabZipper): JSX.Element[] {
  const component = tab.getTab().component;
  if (component) {
    return [viewComponentToRouteComponent(tab.getTab().id, computeTabZipRoute(tab), component)];
  }
  return flatMap((section) => getSectionContentArea(section), tab.getSections());
}

export function getContentArea(tenantZip: BoundTenantZipper): JSX.Element[] {
  const components = flatMap((tabZip) => getTabContentArea(tabZip), tenantZip.getTabs());
  return components;
}

export function getFirstMFPFavoriteComponentPath(tabZip: BoundTab): string {
  let path = '__notfound__';
  const pathfound = tabZip.boundSections?.some((section) => {
    return section.boundViews.some((view) => {
      if ((view.component as unknown) === Favorite) {
        path = `${section.pathSlot}/${view.pathSlot}`;
        return true;
      }
    });
  });
  if (!pathfound) {
    console.warn('User attempted to navigate to favorite but No MFP Favorite component found.');
  }
  return path;
}

export function getActiveComponentOverflow(tenantZip: BoundTenantZipper, routerLocationPath: string): string {
  let overflow = 'hidden';
  tenantZip.getTabs().forEach((tabZip) =>
    tabZip.getSections().forEach((section) => {
      section.getViews().forEach((view) => {
        const path = computeViewZipRoute(view);
        if (routerLocationPath === path) {
          const defaultOverflow = view.getView().overflow;
          if (defaultOverflow) {
            overflow = defaultOverflow;
          }
        }
      });
    })
  );
  return overflow;
}

export const getKeyFromRouterSearch = (
  routerLocation: WithRouterProps['routerLocation'],
  key: string
): string | (string | null)[] | null => {
  const search = queryString.parse(routerLocation.search);
  return search[key];
};

// to get user info from local storage
function parseIdToken() {
  const raw = localStorage.getItem('id_token');
  if (!raw) return {};
  const parts = raw.split('.');
  if (parts.length < 2) return {};
  try {
    const payload = JSON.parse(atob(parts[1]));
    return {
      userEmail: (payload.email as string) || undefined,
      userId: (payload.sub as string) || undefined,
    };
  } catch {
    return {};
  }
}
/** Gets necessary debug info & formats it for display */
export function gatherDebugInfo(): DebugInfo {
  const state = store.getState();
  // break down view
  const activePage: string = state.perspective.activePage || "";
  const activeSubPage: string | undefined = state.perspective.activeSubPage;
  const parts = activePage.split("/");
  const tabSlot = parts[2] || "";
  const sectionSlot = parts[3] || "";
  const viewSlot = parts[4] || "";

  // get confdefn so we can find the BoundView
  const tenantConfig: UIConfDefn | null = state.appConfig.tenantConfig || null;

  let mainComponentType = "Unknown";
  let isWorklist = false;
  let worklistTabComponentType: string | undefined;

  const configFilePaths: string[] = [];
  const pivotFilePaths: string[] = [];

  if (tenantConfig && tabSlot && sectionSlot && viewSlot) {
    const matchingTab = tenantConfig.tabs.find(t => t.pathSlot === tabSlot);
    // @ts-ignore
    const matchingSection = matchingTab?.leftNavSections.find(s => s.pathSlot === sectionSlot);
    // @ts-ignore
    const matchingView = matchingSection?.views.find(v => v.pathSlot === viewSlot);

    if (matchingView) {
      mainComponentType = matchingView.component;
      isWorklist = mainComponentType === "Worklist";

      if (isWorklist) {
        // ——— Worklist: configs = [$id, ...nested.viewDefns], pivots = defns.models/model ———
        const worklistDefn = state.worklist?.viewDefn;

        // use $id, else fallback to defns.view[0] for worklist viewdefn
        let mainId: string | undefined = worklistDefn?.$id;
        if (!mainId) {
          const defns = matchingView.componentProps?.defns as any;
          if (defns?.view && Array.isArray(defns.view) && defns.view.length > 0) {
            mainId = defns.view[0];
          }
        }
        if (mainId) {
          configFilePaths.push(mainId);
        }

        if (worklistDefn?.tabs?.length) {
          const nested =
            (activeSubPage && worklistDefn.tabs.find(t => t.pathSlot === activeSubPage))
            || worklistDefn.tabs[0];

          worklistTabComponentType = nested.componentType;
          configFilePaths.push(...(nested.viewDefns ?? []));

          if (worklistTabComponentType === 'ConfigurableGrid') {
            const gridProps = (nested as any).componentProps as Record<string, any>;
            Object.entries(gridProps).forEach(([key, api]) => {
              const defnId = api?.params?.defnId;
              if (typeof defnId !== 'string') return;

              if (key === 'configApi') {
                // only configApi => configs
                configFilePaths.push(defnId);
              } else if (key.endsWith('Api')) {
                // every other *Api (dataApi, floorsetApi, planningApi, topAttributesApi…)
                // => pivots
                pivotFilePaths.push(defnId);
              }
            });
          }
        }

        // grab the worklist’s own defns.model or defns.models
        const defns = matchingView.componentProps?.defns;
        if (defns) {
          if (Array.isArray(defns.models)) {
            pivotFilePaths.push(...defns.models);
          } else if (typeof defns.model === "string") {
            pivotFilePaths.push(defns.model);
          }
        }
      } else if (matchingView.component === "ConfigurableGrid") {
        // special case, different config - get the defns from each 'api'
        const props = matchingView.componentProps as Record<string, any>;
        Object.entries(props).forEach(([key, api]) => {
          const defnId = api?.params?.defnId;
          if (typeof defnId !== "string") return;

          if (key === "configApi") {
            // only configApi goes into configFilePaths
            configFilePaths.push(defnId);
          } else if (key.endsWith("Api")) {
            // every other *Api (dataApi, floorsetApi, planningApi, topAttributesApi, etc)
            // goes into pivotFilePaths
            pivotFilePaths.push(defnId);
          }
        });
      } else {
        // all other views, check for any available defns
        const props = matchingView.componentProps as any;
        // configApi.defnId
        const cfgId = props.configApi?.params?.defnId;
        if (typeof cfgId === "string") {
          configFilePaths.push(cfgId);
        }

        // defns
        const defns = props.defns as any;
        if (defns) {
          // models/model > pivots
          if (Array.isArray(defns.models)) {
            pivotFilePaths.push(...defns.models);
          } else if (typeof defns.model === "string") {
            pivotFilePaths.push(defns.model);
          }

          // view > configs
          if (Array.isArray(defns.view)) {
            configFilePaths.push(...defns.view);
          }

          // subheader strings > configs (groupyBy... etc)
          if (defns.subheader && typeof defns.subheader === "object") {
            (Object.values(defns.subheader) as string[])
              .forEach(s => configFilePaths.push(s));
          }
        }

        if (mainComponentType === "StyleEdit" && matchingTab) {
          // Find the page slice for this perspective
          const pageKey = matchingTab.id.charAt(0).toLowerCase() + matchingTab.id.slice(1);
          const pages = (state as any).pages || {};
          const pageSlice = pages[pageKey] ?? null;
          if (pageSlice) {
            const viewKey = "styleEdit";
            const pageState = pageSlice[viewKey] as any;

            // Grab its sectionsViewDefns
            const sectionDefs: string[] =
              pageState?.viewDefn?.sectionsViewDefns || [];
            configFilePaths.push(...sectionDefs)
          }
        }
      }
    }
  }
  const currentTime = new Date().toLocaleString();

  // TODO: figure out how to get query params sent to pivot
  // const queryParams = {};

  // favorites stuff
  let favoriteActive = false;
  let favoriteName: string | undefined;
  let favoriteId: string | undefined;

  const favList: any[] = state.subheader?.favoritesList || [];
  const activeFav = favList.find(f => f.active);

  if (activeFav) {
    favoriteActive = true;
    favoriteName = activeFav.jsonBlob.name;
    favoriteId = activeFav.jsonBlob.id;
  }

  // parse user info from JWTs
  const { userEmail, userId } = parseIdToken();

  let scope = '';
  const scopeSlice = state.scope?.scope;
  if (scopeSlice) {
    const { productMemberName, locationMemberName, assortmentWeekRange } = scopeSlice;
    if (productMemberName && locationMemberName && assortmentWeekRange) {
      scope = `Product - ${productMemberName} | Location - ${locationMemberName} | ${assortmentWeekRange.start} to ${assortmentWeekRange.end}`;
    }
  }

    // active filter selections
    const filtersSlice = state.filters?.state ? state.filters.state : [];
    const activeFilters: Record<string, string[]> = {};

    filtersSlice.forEach((ctx: any) => {
      (ctx.filters || []).forEach((flt: any) => {
        // find any values marked `selected===true`
        const picked = (flt.filterValues || [])
          .filter((fv: any) => fv.selected)
          .map((fv: any) => fv.id);

        if (picked.length) {
          activeFilters[flt.name] = picked;
        }
      });
    });

  return {
    mainComponentType,
    isWorklist,
    worklistTabComponentType,
    configFilePaths,
    pivotFilePaths,
    currentTime,
    // queryParams,
    favoriteActive,
    favoriteName,
    favoriteId,
    userEmail,
    userId,
    scope,
    filters: activeFilters,
  };
}
